/*!
 * @Author: Cascade
 * @Date: 2026-02-02
 * @Introduction: OIDC 服务接口
 * @Description: 处理 OIDC 登录相关的后端 API 调用
 */

import xhr from './xhr/index'

/**
 * Token 交换请求参数
 */
interface IExchangeTokenParams {
    code: string   // 授权码
    state: string  // 状态参数
    nonce: string  // 防重放参数
}

/**
 * Token 交换响应数据
 */
export interface IExchangeTokenResponse {
    status: number
    data: {
        token: string      // 业务会话 Token
        id: string         // 用户 ID
        name?: string      // 用户名称
        avatar?: string    // 用户头像
        role?: number      // 用户角色
    }
    message: string
}

/**
 * OIDC 登出请求参数
 */
interface ILogoutParams {
    token?: string  // 当前会话 Token（可选）
}

/**
 * OIDC 登出响应数据
 */
export interface ILogoutResponse {
    status: number
    data: {
        logout_url?: string  // IDP 登出 URL（可选，用于单点登出）
    }
    message: string
}

/**
 * OIDC 服务类
 * 负责与后端 OIDC 相关接口通信
 * 注意：所有敏感操作（如 Token 验证、解析）均由后端完成
 */
class OIDCService {
    /**
     * 将授权码交换为业务 Token
     * 前端仅负责传递 code，不解析任何 Token
     * @param params 包含 code 和 state 的参数
     * @returns 后端返回的业务会话信息
     */
    public async exchangeToken(params: IExchangeTokenParams): Promise<IExchangeTokenResponse> {
        const result = await xhr({
            method: 'post',
            url: '/oidc/exchange-token',
            params: params
        })
        return result as IExchangeTokenResponse
    }

    /**
     * OIDC 登出
     * 调用后端登出接口，清除服务端会话
     * @param params 登出参数
     * @returns 登出响应，可能包含 IDP 登出 URL
     */
    public async logout(params?: ILogoutParams): Promise<ILogoutResponse> {
        const result = await xhr({
            method: 'post',
            url: '/oidc/logout',
            params: params || {}
        })
        return result as ILogoutResponse
    }
}

// 实例化后导出，全局单例
export default new OIDCService()
